import { supabase } from './supabase';

/**
 * superAdmin.ts — client for the server-side super-admin database functions.
 *
 * SECURITY MODEL: every function below calls a Postgres RPC (SECURITY DEFINER)
 * that re-verifies the caller is an active admin/executive INSIDE the database.
 * The app holds no privileges of its own — if a non-admin calls these, the
 * database refuses. Passwords are hashed by Postgres (pgcrypto/bcrypt) and
 * never stored or logged client-side.
 */

export interface StaffUser {
  email: string;
  name: string;
  role: string;
  is_active: boolean;
  discount_limit: number;
  last_login: string | null;
  confirmed: boolean;
}

export const STAFF_ROLES = [
  { value: 'admin', label: 'Admin (full access)' },
  { value: 'executive', label: 'Executive / Director (full access)' },
  { value: 'manager', label: 'Manager (catalog + approvals)' },
  { value: 'sales_rep', label: 'Sales Rep (quotations)' },
  { value: 'accountant', label: 'Accountant (reports + quotations)' },
  { value: 'marketing', label: 'Marketing (parties + campaigns)' },
] as const;

function rpcError(result: { error: { message: string } | null }): string {
  const raw = result.error?.message || 'Unknown database error';
  // Strip Postgres noise for user-friendly messages
  if (raw.includes('Only Super Admins')) return 'Only Super Admins can do this.';
  if (raw.includes('already exists')) return 'A user with this email already exists.';
  if (raw.includes('User not found') || raw.includes('Profile not found')) return 'User not found (they may have been deleted).';
  if (raw.includes('Invalid role')) return 'Invalid role selected.';
  if (raw.includes('at least 10 characters')) return 'Password must be at least 10 characters.';
  if (raw.includes('duplicate key')) return 'A user with this email already exists.';
  return raw;
}

/** True when the signed-in user may use staff control (checked server-side). */
export async function checkSuperAdmin(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('is_super_admin');
    if (error) return false;
    return data === true;
  } catch {
    return false;
  }
}

export async function listStaffUsers(): Promise<StaffUser[]> {
  const { data, error } = await supabase.rpc('superadmin_list_users');
  if (error) throw new Error(rpcError({ error }));
  return (data || []).map((u: Record<string, unknown>) => ({
    email: String(u.email),
    name: String(u.name || u.email),
    role: String(u.role || 'sales_rep'),
    is_active: u.is_active !== false,
    discount_limit: Number(u.discount_limit ?? 10),
    last_login: (u.last_login as string) || null,
    confirmed: u.confirmed !== false,
  }));
}

export async function createStaffUser(params: {
  email: string;
  password: string;
  name: string;
  role: string;
}): Promise<void> {
  const { error } = await supabase.rpc('superadmin_create_user', {
    p_email: params.email.trim().toLowerCase(),
    p_password: params.password,
    p_name: params.name.trim(),
    p_role: params.role,
  });
  if (error) throw new Error(rpcError({ error }));
}

export async function resetStaffPassword(email: string, newPassword: string): Promise<void> {
  const { error } = await supabase.rpc('superadmin_reset_password', {
    p_email: email,
    p_new_password: newPassword,
  });
  if (error) throw new Error(rpcError({ error }));
}

export async function setStaffActive(email: string, active: boolean): Promise<void> {
  const { error } = await supabase.rpc('superadmin_toggle_user', {
    p_email: email,
    p_active: active,
  });
  if (error) throw new Error(rpcError({ error }));
}

export async function deleteStaffUser(email: string): Promise<void> {
  const { error } = await supabase.rpc('superadmin_delete_user', {
    p_email: email,
  });
  if (error) throw new Error(rpcError({ error }));
}
